import { useState } from 'react';
import { Pencil, Check, ArrowRight } from 'lucide-react';
import {
  CUSTOM_OPTION_VALUE,
  hasAnswerButtons,
  type QualificationAnswerPayload,
  type QualificationOption,
  type QualificationOptionsPayload,
} from '../api';
import { cleanQuestionText } from '../utils';

/**
 * The discovery / qualification question rendered inside a common Claude-like card.
 *
 * Encloses the bold question heading, all selectable options, and a Skip action
 * in a common rounded rectangle container matching the Vinfotech brand theme.
 */
export function QualificationOptions({
  data,
  theme,
  primaryColor = '#6720ff',
  disabled,
  expanded,
  hideQuestionHeader,
  onAnswer,
  onChooseCustom,
  onSkip,
}: {
  data: QualificationOptionsPayload;
  theme: 'light' | 'dark';
  primaryColor: string;
  /** True while a turn is in flight, or once this is no longer the newest turn. */
  disabled: boolean;
  expanded?: boolean;
  hideQuestionHeader?: boolean;
  onAnswer: (text: string, payload: QualificationAnswerPayload) => void;
  /**
   * Point the composer at this question, or `null` to release it.
   */
  onChooseCustom: (data: QualificationOptionsPayload | null) => void;
  /** Skip / dismiss this question. */
  onSkip?: () => void;
}) {
  const isMulti = data.answer_type === 'multi_choice_with_custom';
  const dark = theme === 'dark';

  const [picked, setPicked] = useState<string[]>([]);
  const [hovered, setHovered] = useState<string | null>(null);

  const options = data.options ?? [];
  const chosen = options.filter(
    (o) => picked.includes(o.value) && o.value !== CUSTOM_OPTION_VALUE
  );

  const questionTitle = cleanQuestionText(data.question);

  const send = (values: string[]) => {
    const list = values
      .map((v) => options.find((o) => o.value === v))
      .filter((o): o is QualificationOption => Boolean(o));
    if (list.length === 0) return;

    onAnswer(list.map((o) => o.label).join(', '), {
      question_id: data.question_id,
      answer_type: data.answer_type,
      selected: list.map((o) => ({ value: o.value, label: o.label })),
      is_custom: false,
    });
  };

  const click = (option: QualificationOption) => {
    if (disabled) return;

    if (option.value === CUSTOM_OPTION_VALUE) {
      const already = picked.includes(CUSTOM_OPTION_VALUE);
      setPicked(already ? [] : [CUSTOM_OPTION_VALUE]);
      onChooseCustom(already ? null : data);
      return;
    }

    if (picked.includes(CUSTOM_OPTION_VALUE)) onChooseCustom(null);

    if (!isMulti) {
      setPicked([option.value]);
      send([option.value]);
      return;
    }

    setPicked((prev) => {
      const without = prev.filter((v) => v !== CUSTOM_OPTION_VALUE);
      return without.includes(option.value)
        ? without.filter((v) => v !== option.value)
        : [...without, option.value];
    });
  };

  if (!hasAnswerButtons(data)) return null;

  return (
    <div
      className="vw-adv-qual-card"
      style={{
        marginTop: '12px',
        marginBottom: '6px',
        width: '100%',
        boxSizing: 'border-box',
        borderRadius: '16px',
        border: `1px solid ${dark ? '#373050' : '#e7e2f3'}`,
        background: dark ? '#1e1a2f' : '#ffffff',
        padding: expanded ? '18px 20px' : '15px 16px',
        boxShadow: dark
          ? '0 4px 20px -2px rgba(0, 0, 0, 0.4), 0 1px 3px rgba(0, 0, 0, 0.3)'
          : '0 4px 20px -2px rgba(103, 32, 255, 0.05), 0 2px 6px -1px rgba(0, 0, 0, 0.03)',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        animation: 'smoothFadeIn .3s cubic-bezier(0.16, 1, 0.3, 1) both',
      }}
    >
      {/* Question Header (in bold, clearly distinct) */}
      {!hideQuestionHeader && questionTitle && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '12px',
            marginBottom: '2px',
          }}
        >
          <div
            style={{
              fontSize: expanded ? '15.5px' : '14.5px',
              fontWeight: 700,
              color: dark ? '#f5f3fa' : '#181426',
              lineHeight: 1.4,
              letterSpacing: '-0.01em',
              flex: 1,
            }}
          >
            {questionTitle}
          </div>
          {isMulti && (
            <span
              style={{
                fontSize: '11px',
                fontWeight: 600,
                padding: '3px 9px',
                borderRadius: '9999px',
                background: dark ? '#342956' : '#f3eeff',
                color: primaryColor,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                marginTop: '1px',
              }}
            >
              Select all that apply
            </span>
          )}
        </div>
      )}

      {/* Options List */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          width: '100%',
        }}
      >
        {options.map((option, idx) => {
          const active = picked.includes(option.value);
          const hot = hovered === option.value && !disabled;
          const custom = option.value === CUSTOM_OPTION_VALUE;

          return (
            <button
              key={option.value}
              type="button"
              disabled={disabled}
              onClick={() => click(option)}
              onMouseEnter={() => !disabled && setHovered(option.value)}
              onMouseLeave={() => setHovered((h) => (h === option.value ? null : h))}
              className="vw-adv-qual-option-btn"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                width: '100%',
                minHeight: '44px',
                boxSizing: 'border-box',
                padding: expanded ? '11px 16px' : '10px 14px',
                borderRadius: '12px',
                border: '1px solid',
                borderColor: active
                  ? primaryColor
                  : hot
                  ? '#cdbcfa'
                  : dark
                  ? '#383250'
                  : '#eeebf5',
                background: active
                  ? dark
                    ? '#332757'
                    : '#ece5fc'
                  : hot
                  ? dark
                    ? '#2b2544'
                    : '#f5f1fe'
                  : dark
                  ? '#252136'
                  : '#faf9fd',
                boxShadow: active
                  ? `0 0 0 1px ${primaryColor}`
                  : hot
                  ? '0 2px 8px rgba(103, 32, 255, 0.08)'
                  : 'none',
                textAlign: 'left',
                cursor: disabled ? 'not-allowed' : 'pointer',
                transition:
                  'border-color 0.16s ease, background 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease',
                transform: hot && !disabled ? 'translateY(-1px)' : 'none',
                fontFamily: 'inherit',
                outline: 'none',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '11px',
                  minWidth: 0,
                  flex: 1,
                }}
              >
                {/* Index / Choice Badge */}
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '24px',
                    height: '24px',
                    minWidth: '24px',
                    borderRadius: '6px',
                    border:
                      isMulti && !active
                        ? `1.5px solid ${dark ? '#4a4268' : '#d2cce1'}`
                        : 'none',
                    background: active
                      ? primaryColor
                      : hot
                      ? dark
                        ? '#3a305e'
                        : '#e7dff8'
                      : dark
                      ? '#2f2945'
                      : '#ede9f6',
                    color: active
                      ? '#ffffff'
                      : hot
                      ? primaryColor
                      : dark
                      ? '#b8b2cb'
                      : '#575168',
                    fontSize: '11.5px',
                    fontWeight: 600,
                    flexShrink: 0,
                    transition: 'all 0.16s ease',
                  }}
                >
                  {isMulti && active ? (
                    <Check size={13} strokeWidth={2.5} />
                  ) : custom ? (
                    <Pencil size={11.5} />
                  ) : (
                    idx + 1
                  )}
                </span>

                {/* Option Content */}
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontSize: expanded ? '14.5px' : '13.5px',
                      fontWeight: 500,
                      color: active
                        ? dark
                          ? '#ffffff'
                          : '#191528'
                        : dark
                        ? '#e6e3f0'
                        : '#221e31',
                      lineHeight: 1.35,
                      wordBreak: 'break-word',
                    }}
                  >
                    {option.label}
                  </div>
                  {(option.description || custom) && (
                    <div
                      style={{
                        fontSize: expanded ? '12.5px' : '12px',
                        color: dark ? '#9f98b5' : '#736e82',
                        lineHeight: 1.35,
                        marginTop: '2px',
                        wordBreak: 'break-word',
                      }}
                    >
                      {option.description ??
                        'None of these fit. Type your own answer instead.'}
                    </div>
                  )}
                </div>
              </div>

              {/* Arrow on hover / active (Claude feel) */}
              {!isMulti && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '18px',
                    height: '18px',
                    flexShrink: 0,
                    opacity: hot || active ? 1 : 0,
                    transform: hot || active ? 'translateX(0)' : 'translateX(-4px)',
                    transition: 'opacity 0.16s ease, transform 0.16s ease',
                    color: primaryColor,
                  }}
                >
                  <ArrowRight size={15} />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Card Footer (Skip button & Multi-select Send) */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
          marginTop: '2px',
          paddingTop: '2px',
        }}
      >
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            if (disabled) return;
            if (onSkip) onSkip();
            else onChooseCustom(null);
          }}
          className="vw-adv-qual-skip-btn"
          style={{
            border: `1px solid ${dark ? '#3a3452' : '#dfd9ee'}`,
            background: dark ? '#242035' : '#ffffff',
            color: dark ? '#aba4c0' : '#615c71',
            fontSize: '12.5px',
            fontWeight: 500,
            borderRadius: '8px',
            padding: '6px 14px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
            transition: 'all 0.15s ease',
            opacity: disabled ? 0.5 : 1,
            outline: 'none',
          }}
          onMouseEnter={(e) => {
            if (!disabled) {
              e.currentTarget.style.background = dark ? '#2d2742' : '#f5f2fb';
              e.currentTarget.style.color = dark ? '#ffffff' : '#191528';
              e.currentTarget.style.borderColor = dark ? '#4d436c' : '#c8c0df';
            }
          }}
          onMouseLeave={(e) => {
            if (!disabled) {
              e.currentTarget.style.background = dark ? '#242035' : '#ffffff';
              e.currentTarget.style.color = dark ? '#aba4c0' : '#615c71';
              e.currentTarget.style.borderColor = dark ? '#3a3452' : '#dfd9ee';
            }
          }}
        >
          Skip
        </button>

        {isMulti && chosen.length > 0 && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => send(picked)}
            style={{
              backgroundColor: primaryColor,
              color: '#ffffff',
              border: 0,
              borderRadius: '8px',
              padding: '7px 16px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: disabled ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              transition: 'all 0.15s ease',
              boxShadow: '0 2px 8px rgba(103, 32, 255, 0.25)',
              outline: 'none',
            }}
            onMouseEnter={(e) => {
              if (!disabled) e.currentTarget.style.opacity = '0.9';
            }}
            onMouseLeave={(e) => {
              if (!disabled) e.currentTarget.style.opacity = '1';
            }}
          >
            Send {chosen.length} answer{chosen.length === 1 ? '' : 's'}
          </button>
        )}
      </div>
    </div>
  );
}
